// covered-by: packages/schemas/src/christo/__tests__/askPunttReview.test.ts
/**
 * AskPuntt V2 review document. Canonical store is AskPunttReview
 * (nested comments). The FE speaks reviewId; there is no Ticket adapter.
 */
import { z } from "zod";

import {
  cellAnchorSchema,
  commentBboxSchema,
  ticketMessageCitationSchema,
} from "../commons/ticket";

export const askPunttReviewFileSchema = z.object({
  source: z.string(),
  name: z.string().optional(),
  type: z.enum(["image", "video", "file"]).optional(),
  thumbnailUrl: z.string().nullable().optional(),
});
export type AskPunttReviewFile = z.infer<typeof askPunttReviewFileSchema>;

export const askPunttReviewCommentsSummarySchema = z.object({
  ai: z.number().int().nonnegative(),
  human: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
});
export type AskPunttReviewCommentsSummary = z.infer<
  typeof askPunttReviewCommentsSummarySchema
>;

export const askPunttCommentVisibilitySchema = z.union([
  z.literal("all"),
  z.string().min(1),
]);
export type AskPunttCommentVisibility = z.infer<
  typeof askPunttCommentVisibilitySchema
>;

export const askPunttReviewCommentSeveritySchema = z.enum([
  "high",
  "medium",
  "low",
]);
export type AskPunttReviewCommentSeverity = z.infer<
  typeof askPunttReviewCommentSeveritySchema
>;

export const askPunttReviewCommentVoteSchema = z.object({
  user: z.string(),
  vote: z.string(),
  createdAt: z.string().datetime(),
});

export const askPunttReviewCommentMessageSchema = z.object({
  _id: z.string(),
  createdBy: z.string().optional(),
  description: z.string(),
  isAI: z.boolean().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  mentions: z.array(z.string()).optional(),
  citations: z.array(ticketMessageCitationSchema).optional(),
});
export type AskPunttReviewCommentMessage = z.infer<
  typeof askPunttReviewCommentMessageSchema
>;

/**
 * Why a finding is shown demoted: a reviewer already rejected this finding, on
 * this or another artwork. Carried onto the comment so the list can label it --
 * a demotion the reader cannot see is indistinguishable from a bug.
 */
/**
 * Who made a judgment, read from the ledger when the review is served. The
 * user id lets the reader tell her own decision ("You rejected this before")
 * from a colleague's; the name is `users.name`, never an email.
 */
export const judgmentDeciderSchema = z.object({
  userId: z.string(),
  name: z.string().optional(),
});
export type JudgmentDecider = z.infer<typeof judgmentDeciderSchema>;

export const previouslyRejectedSchema = z.object({
  judgmentId: z.string(),
  remembered: z.string(),
  matchedBy: z.enum(["text", "vector"]),
  score: z.number(),
  role: z.string().optional(),
  // Where the judgment is logged: its knowledge rule, else its review.
  ruleId: z.string().optional(),
  reviewId: z.string().optional(),
  chatId: z.string().optional(),
  // Added on read from `reviewjudgments`, so a receipt stored before these
  // existed names its decider too. Absent when the ledger row has no user.
  decidedBy: judgmentDeciderSchema.optional(),
  decidedAt: z.string().optional(),
});
export type PreviouslyRejected = z.infer<typeof previouslyRejectedSchema>;

/**
 * Where a finding sits, in the terms warhol folds duplicate findings on.
 *
 * `services/warhol/agents/primary/review_outcomes.py::merge_duplicates` keeps
 * one finding per defect: same document, same page, same approved wording, and
 * overlapping boxes (or one pin inside the other's box). Warhol does not carry
 * which intents were folded into which, so the review writer keeps this
 * projection on every AI comment to recognise a fold after the fact — a
 * finding a person judged while the turn ran is kept and linked to the one it
 * was folded into rather than silently deleted.
 */
export const askPunttReviewCommentAnchorSchema = z.object({
  document: z.string(),
  page: z.number(),
  /** The approved wording the finding rests on, or `area:<area>` when it rests on none. */
  wording: z.string(),
  bbox: commentBboxSchema.optional(),
  pinX: z.number().optional(),
  pinY: z.number().optional(),
});
export type AskPunttReviewCommentAnchor = z.infer<
  typeof askPunttReviewCommentAnchorSchema
>;

/**
 * The finding a judged comment was folded into at the end of the turn.
 *
 * why a person sees this at all: she judged the provisional finding while the
 * rest of the review ran, and warhol's dedup then folded it into another
 * finding. Dropping it would erase a judgment she made; keeping it without a
 * word would show two comments for one defect. So it stays with a pointer at
 * the survivor.
 */
export const askPunttReviewCommentFoldedIntoSchema = z.object({
  /** The surviving finding's own text, so the line reads on its own. */
  description: z.string(),
  commentId: z.string().optional(),
  intentId: z.string().optional(),
});
export type AskPunttReviewCommentFoldedInto = z.infer<
  typeof askPunttReviewCommentFoldedIntoSchema
>;

/**
 * The same finding as a comment of an earlier review of this artwork, made in
 * another chat (warhol spec surbhi.kapoor/0061). A link, not a judgment:
 * `disposition` is what a person did with that comment, and `decision` is the
 * judgment ledger's own sentence when it holds one. The serializer strips
 * Mongo's nulls before this parses, so the optional fields are absent, never
 * null.
 */
export const askPunttReviewEarlierCommentSchema = z.object({
  reviewId: z.string(),
  chatId: z.string(),
  commentId: z.string(),
  disposition: z.enum(["open", "accepted", "rejected"]),
  decision: z.string().optional(),
  createdAt: z.string().optional(),
  // The ledger's rejection of that comment, read when the review is served:
  // the card words the decision from these, not from `decision`.
  decidedBy: judgmentDeciderSchema.optional(),
  decidedAt: z.string().optional(),
});
export type AskPunttReviewEarlierComment = z.infer<
  typeof askPunttReviewEarlierCommentSchema
>;

export const askPunttReviewCommentSchema = z.object({
  _id: z.string(),
  createdBy: z.string().optional(),
  aiPersona: z
    .object({
      name: z.string(),
      avatar: z.string(),
    })
    .optional(),
  description: z.string(),
  isAI: z.boolean(),
  isPending: z.boolean(),
  isDeleted: z.boolean(),
  isReview: z.boolean(),
  publishedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  mentions: z.array(z.string()),
  citations: z.array(ticketMessageCitationSchema),
  likedBy: z.string().optional(),
  liked: z.boolean().nullable().optional(),
  previouslyRejected: previouslyRejectedSchema.nullish(),
  previouslyConfirmed: previouslyRejectedSchema.nullish(),
  votes: z.array(askPunttReviewCommentVoteSchema).optional(),
  messages: z.array(askPunttReviewCommentMessageSchema),
  x: z.number().optional(),
  y: z.number().optional(),
  rel_x: z.number().optional(),
  rel_y: z.number().optional(),
  page_num: z.number().optional(),
  bbox: commentBboxSchema.optional(),
  cellAnchor: cellAnchorSchema.optional(),
  videoStart: z.number().optional(),
  videoEnd: z.number().optional(),
  locationDescription: z.string().optional(),
  isRequired: z.boolean(),
  rule: z.string().optional(),
  visibility: askPunttCommentVisibilitySchema.optional(),
  editedBy: z.string().optional(),
  editedAt: z.string().datetime().optional(),
  severity: askPunttReviewCommentSeveritySchema.optional(),
  /**
   * Who raised the finding -- the reviewer or check names warhol's review
   * carries (`nutrition`, `regulated-copy`, `copy-diff`, ...). What the
   * comments list groups and filters by. Absent on comments a person wrote
   * and on reviews made before the field existed.
   */
  tags: z.array(z.string()).optional(),
  /**
   * The warhol intent this finding verifies (`EvidenceFinding.intent_id`).
   *
   * The key the two writes of one review merge on. A review is written twice:
   * once provisionally, a comment per verified finding while the turn is still
   * running, and once at the end, when warhol's whole outcome is known. The
   * second write matches the first's comments by this id, so a person's
   * confirm/reject on a provisional finding survives the turn ending and no
   * finding is ever stored twice. Absent on human comments and on AI comments
   * written before this field existed.
   */
  intentId: z.string().optional(),
  /** Where this finding sits, for recognising a fold at the end of the turn. */
  aiAnchor: askPunttReviewCommentAnchorSchema.optional(),
  /** Set only on a judged finding that the turn's fold removed from the final set. */
  foldedInto: askPunttReviewCommentFoldedIntoSchema.optional(),
  /** The same finding in an earlier review of this artwork; absent when none raised it. */
  earlierComment: askPunttReviewEarlierCommentSchema.nullish(),
});
export type AskPunttReviewComment = z.infer<typeof askPunttReviewCommentSchema>;

/**
 * How long a blocked check's reason may be, on the wire and in the database.
 *
 * The reasons are one short phrase -- "no text layer", "it ran out of time" --
 * and the product renders them as a line under the check's name. The bound is
 * here because `reason` is the one tenant-visible field on this model whose
 * text a backend hands over rather than the contract naming it, so a future
 * producer that puts a provider error body or a stack-ish string on it would
 * otherwise put the whole of it in a person's browser. One number, shared by
 * the zod schema, the mongoose path (`services/christo/lib/models/askPunttReview.ts`)
 * and the writer that truncates to it (`warholReviewBlocked`), so the three
 * cannot disagree about what fits.
 */
export const ASK_PUNTT_REVIEW_BLOCKED_REASON_MAX = 280;

/**
 * A check that did not finish, and why. Warhol's `ReviewOutcome.blocked`
 * (`contracts/findings.py::BlockedCheck`, spec `roberto.faga/0033`), tagged
 * with the artwork it belongs to.
 *
 * `kind` is the remedy, not a label: `skipped` needs different input (an
 * outlined artwork has no text layer), `failed` a retry, `timed_out` a longer
 * budget, `unresolved` a person.
 */
export const askPunttReviewBlockedSchema = z.object({
  artwork: z.string(),
  check: z.string(),
  kind: z.enum(["skipped", "failed", "timed_out", "unresolved"]),
  reason: z.string().max(ASK_PUNTT_REVIEW_BLOCKED_REASON_MAX).optional(),
});
export type AskPunttReviewBlocked = z.infer<typeof askPunttReviewBlockedSchema>;

export const askPunttReviewSchema = z.object({
  reviewId: z.string(),
  enterpriseId: z.string(),
  chatId: z.string().nullable(),
  userId: z.string(),
  title: z.string(),
  status: z.string(),
  aiStatus: z.string().optional(),
  aiRunning: z.boolean(),
  files: z.array(askPunttReviewFileSchema),
  contextAssets: z.array(askPunttReviewFileSchema),
  commentsSummary: askPunttReviewCommentsSummarySchema,
  comments: z.array(askPunttReviewCommentSchema),
  /** Absent when every check finished, and on reviews written before the field. */
  blocked: z.array(askPunttReviewBlockedSchema).optional(),
  thumbnailUrl: z.string().nullable(),
  reviewWorkflowId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AskPunttReview = z.infer<typeof askPunttReviewSchema>;

/** What a rejection is scoped to, chosen by the reviewer when it is ambiguous. */
export const judgmentScopeSchema = z.object({
  brand: z.string().optional(),
  dimension: z
    .enum(["markets", "productForms", "claimTypes", "regulations"])
    .optional(),
  value: z.string().optional(),
});
export type JudgmentScope = z.infer<typeof judgmentScopeSchema>;

export const askPunttCommentFilterSchema = z.enum([
  "mention_me",
  "severity",
  "persona",
  "liked",
  "open",
]);
export type AskPunttCommentFilter = z.infer<typeof askPunttCommentFilterSchema>;

export const getAskPunttReviewRequestSchema = z.object({
  params: z.object({ reviewId: z.string() }),
});
export const getAskPunttReviewResponseSchema = z.object({
  review: askPunttReviewSchema,
});
export type GetAskPunttReviewResponse = z.infer<
  typeof getAskPunttReviewResponseSchema
>;

export const createAskPunttReviewRequestSchema = z.object({
  body: z
    .object({
      title: z.string().min(1).optional(),
      chatId: z.string().min(1).optional(),
    })
    .strict(),
});
export const createAskPunttReviewResponseSchema = z.object({
  review: askPunttReviewSchema,
});
export type CreateAskPunttReviewResponse = z.infer<
  typeof createAskPunttReviewResponseSchema
>;

export const listAskPunttReviewCommentsRequestSchema = z.object({
  params: z.object({ reviewId: z.string() }),
  query: z.object({
    filter: askPunttCommentFilterSchema.optional(),
    q: z.string().optional(),
    hideRejected: z
      .enum(["true", "false"])
      .optional()
      .transform((value) => value !== "false"),
  }),
});
export const listAskPunttReviewCommentsResponseSchema = z.object({
  comments: z.array(askPunttReviewCommentSchema),
});
export type ListAskPunttReviewCommentsResponse = z.infer<
  typeof listAskPunttReviewCommentsResponseSchema
>;

export const askPunttReviewCommentWriteBodySchema = z.object({
  description: z.string(),
  x: z.coerce.number().optional(),
  y: z.coerce.number().optional(),
  rel_x: z.coerce.number().optional(),
  rel_y: z.coerce.number().optional(),
  page_num: z.coerce.number().optional(),
  videoStart: z.coerce.number().optional(),
  videoEnd: z.coerce.number().optional(),
  bbox: commentBboxSchema.optional(),
  cellAnchor: cellAnchorSchema.optional(),
  locationDescription: z.string().optional(),
  mentions: z.array(z.string()).optional(),
  citations: z.array(ticketMessageCitationSchema).optional(),
  liked: z.boolean().nullable().optional(),
  // Rides with the verdict, never in a second call: two writes could land a
  // rejection with no scope, and an unscoped rejection is the one state that
  // would bind every brand in the enterprise.
  judgmentScope: judgmentScopeSchema.optional(),
  visibility: askPunttCommentVisibilitySchema.optional(),
  isPending: z.boolean().optional(),
  isRequired: z.boolean().optional(),
  isReview: z.boolean().optional(),
  rule: z.string().optional(),
  severity: askPunttReviewCommentSeveritySchema.optional(),
});
export type AskPunttReviewCommentWriteBody = z.infer<
  typeof askPunttReviewCommentWriteBodySchema
>;

export const createAskPunttReviewCommentRequestSchema = z.object({
  params: z.object({ reviewId: z.string() }),
  body: askPunttReviewCommentWriteBodySchema,
});
export const createAskPunttReviewCommentResponseSchema = z.object({
  comment: askPunttReviewCommentSchema,
});
export type CreateAskPunttReviewCommentResponse = z.infer<
  typeof createAskPunttReviewCommentResponseSchema
>;

export const updateAskPunttReviewCommentRequestSchema = z.object({
  params: z.object({ reviewId: z.string(), commentId: z.string() }),
  body: askPunttReviewCommentWriteBodySchema.partial().extend({
    description: z.string().optional(),
  }),
});
export const updateAskPunttReviewCommentResponseSchema = z.object({
  comment: askPunttReviewCommentSchema,
});
export type UpdateAskPunttReviewCommentResponse = z.infer<
  typeof updateAskPunttReviewCommentResponseSchema
>;
